import { roundMoney } from "./formatPrice";
import {
  findPackagePromo,
  getPackageQuantity,
  getPromoId,
} from "../components/routs/sales/promotionUtils";

export const PACKAGE_LINE_KIND = {
  UNIT: "unit",
  BUNDLE: "bundle",
};

const productFromCartItem = (item) => item?.product || item || null;

export function getCartPackagePromo(item) {
  return findPackagePromo(productFromCartItem(item));
}

export function getCartPackQuantity(item) {
  return getPackageQuantity(getCartPackagePromo(item));
}

export function getCartLineUpsertPromoId(product, fallbackPromoId) {
  const packPromo = findPackagePromo(product);
  if (packPromo) return getPromoId(packPromo);
  if (product?.pricing?.promotions?.length > 0) {
    return product.pricing.promotions[0].promoId;
  }
  return fallbackPromoId;
}

export function splitCartQuantity(item) {
  const packQuantity = getCartPackQuantity(item);
  const quantity = Number(item?.quantity) || 0;
  if (packQuantity < 2) {
    return { packQuantity: 0, bundleCount: 0, unitCount: quantity, quantity };
  }
  return {
    packQuantity,
    bundleCount: Math.floor(quantity / packQuantity),
    unitCount: quantity % packQuantity,
    quantity,
  };
}

export function getPackageLineDisplayQty(item, lineKind) {
  const { bundleCount, unitCount, quantity } = splitCartQuantity(item);
  if (lineKind === PACKAGE_LINE_KIND.BUNDLE) return bundleCount;
  if (getCartPackQuantity(item) >= 2) return unitCount;
  return quantity;
}

/** Map a visible +/- / typed qty back to the single backend unit quantity. */
export function totalQuantityForPackageLineDisplay(item, lineKind, displayQty) {
  const { packQuantity, bundleCount, unitCount } = splitCartQuantity(item);
  const nextDisplay = Math.max(0, Number(displayQty) || 0);
  if (packQuantity < 2) return nextDisplay;
  if (lineKind === PACKAGE_LINE_KIND.BUNDLE) {
    return nextDisplay * packQuantity + unitCount;
  }
  return bundleCount * packQuantity + nextDisplay;
}

export function totalQuantityAfterRemovingPackageLine(item, lineKind) {
  const { packQuantity, bundleCount, unitCount } = splitCartQuantity(item);
  if (packQuantity < 2) return 0;
  if (lineKind === PACKAGE_LINE_KIND.BUNDLE) return unitCount;
  return bundleCount * packQuantity;
}

export function totalQuantityAfterSettingBundles(item, bundleCount) {
  const { packQuantity, unitCount, quantity } = splitCartQuantity(item);
  if (packQuantity < 2) return quantity;
  return Math.max(0, Number(bundleCount) || 0) * packQuantity + unitCount;
}

export function capPackageLineDisplayQty(item, lineKind, displayQty, maxUnits) {
  const { packQuantity, bundleCount, unitCount } = splitCartQuantity(item);
  const nextDisplay = Math.max(0, Number(displayQty) || 0);
  if (!Number.isFinite(maxUnits)) return nextDisplay;
  if (packQuantity < 2) {
    return Math.min(nextDisplay, maxUnits);
  }
  if (lineKind === PACKAGE_LINE_KIND.BUNDLE) {
    const maxBundles = Math.max(
      0,
      Math.floor((maxUnits - unitCount) / packQuantity),
    );
    return Math.min(nextDisplay, maxBundles);
  }
  const maxUnitsOnLine = Math.max(0, maxUnits - bundleCount * packQuantity);
  return Math.min(nextDisplay, maxUnitsOnLine);
}

export function getPackageLineTotals(item) {
  const { packQuantity, bundleCount } = splitCartQuantity(item);
  const promo = getCartPackagePromo(item);
  const packagePrice = Number(promo?.discountValue) || 0;
  const itemTotal = Number(item?.totalPrice) || 0;
  if (packQuantity < 2 || bundleCount < 1) {
    return { bundleTotal: 0, unitTotal: itemTotal };
  }
  const bundleTotal = roundMoney(bundleCount * packagePrice);
  const unitTotal = roundMoney(Math.max(0, itemTotal - bundleTotal));
  return { bundleTotal, unitTotal };
}

export function getPackageLineTotalPrice(item, lineKind) {
  const { bundleTotal, unitTotal } = getPackageLineTotals(item);
  if (lineKind === PACKAGE_LINE_KIND.BUNDLE) return bundleTotal;
  return unitTotal;
}

const regularDealNote = (item) => {
  const inSale =
    item?.promoId && item.quantity * item.unitPrice !== item.totalPrice;
  const savedStr = inSale
    ? (item.unitPrice * item.quantity - item.totalPrice).toFixed(2)
    : null;
  return (
    [
      item?.promoId ? "במבצע" : null,
      inSale && savedStr ? `חסכת ${savedStr} ₪` : null,
    ]
      .filter(Boolean)
      .join(" · ") || null
  );
};

export function getPackageLineDealNote(item, lineKind) {
  const { packQuantity, bundleCount } = splitCartQuantity(item);
  if (packQuantity >= 2) {
    if (lineKind !== PACKAGE_LINE_KIND.BUNDLE) return null;
    const { bundleTotal } = getPackageLineTotals(item);
    const listPrice = Number(productFromCartItem(item)?.pricing?.price);
    const saved =
      Number.isFinite(listPrice) && packQuantity
        ? roundMoney(listPrice * packQuantity * bundleCount - bundleTotal)
        : 0;
    const parts = ["מארז"];
    if (packQuantity > 0) parts.push(`${packQuantity} יח' במארז`);
    if (saved > 0.001) parts.push(`חסכת ${saved.toFixed(2)} ₪`);
    return parts.join(" · ");
  }
  return regularDealNote(item);
}

export function expandCartItemsToPackageLines(items = []) {
  const lines = [];
  for (const item of items || []) {
    const { packQuantity, bundleCount, unitCount, quantity } =
      splitCartQuantity(item);
    if (packQuantity < 2 || quantity === 0 || bundleCount < 1) {
      lines.push({
        item,
        lineKind: PACKAGE_LINE_KIND.UNIT,
        lineKey: item.productId,
        displayQty: quantity,
        packQuantity,
        lineTotal: Number(item.totalPrice) || 0,
        isBundleLine: false,
        dealNote: getPackageLineDealNote(item, PACKAGE_LINE_KIND.UNIT),
      });
      continue;
    }
    lines.push({
      item,
      lineKind: PACKAGE_LINE_KIND.BUNDLE,
      lineKey: `${item.productId}::bundle`,
      displayQty: bundleCount,
      packQuantity,
      lineTotal: getPackageLineTotalPrice(item, PACKAGE_LINE_KIND.BUNDLE),
      isBundleLine: true,
      dealNote: getPackageLineDealNote(item, PACKAGE_LINE_KIND.BUNDLE),
    });
    if (unitCount > 0) {
      lines.push({
        item,
        lineKind: PACKAGE_LINE_KIND.UNIT,
        lineKey: `${item.productId}::unit`,
        displayQty: unitCount,
        packQuantity,
        lineTotal: getPackageLineTotalPrice(item, PACKAGE_LINE_KIND.UNIT),
        isBundleLine: false,
        dealNote: getPackageLineDealNote(item, PACKAGE_LINE_KIND.UNIT),
      });
    }
  }
  return lines;
}
